import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Images, Sparkles, FileText, TrendingUp, BarChart3, Inbox, MessageSquare, Users, Handshake, Settings, UserCog, LogOut, Menu, X, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl } from "@/lib/api";
import { COMPANY } from "@/data/company";
import { Switch } from "@/components/ui/switch";

const NAV = [
  { to: "/admin", label: "Tableau de bord", icon: LayoutDashboard, end: true },
  { to: "/admin/leads", label: "Leads", icon: Inbox },
  { to: "/admin/chat", label: "Chat en direct", icon: MessageSquare },
  { to: "/admin/realisations", label: "Réalisations", icon: Images },
  { to: "/admin/seo", label: "Générateur SEO", icon: Sparkles },
  { to: "/admin/articles", label: "Articles & pages", icon: FileText },
  { to: "/admin/keywords", label: "Positions SEO", icon: TrendingUp },
  { to: "/admin/analytics", label: "Trafic", icon: BarChart3 },
  { to: "/admin/team", label: "Équipe", icon: Users },
  { to: "/admin/partners", label: "Partenaires", icon: Handshake },
  { to: "/admin/settings", label: "Paramètres", icon: Settings },
  { to: "/admin/account", label: "Mon compte", icon: UserCog },
];

export default function AdminLayout() {
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [available, setAvailable] = useState(!!user?.is_available);

  useEffect(() => { setAvailable(!!user?.is_available); }, [user]);

  const toggleAvailable = async (v) => {
    setAvailable(v);
    try {
      await api.put(`/admin/agents/${user.id}`, { is_available: v });
      setUser({ ...user, is_available: v });
    } catch { setAvailable(!v); }
  };

  const doLogout = () => { logout(); nav("/admin/login"); };

  return (
    <div className="min-h-screen bg-muted/40 flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-ink text-white flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-[72px] flex items-center gap-2 px-5 border-b border-white/10">
          <img src={fileUrl(COMPANY.logo)} alt="SMT" className="h-9 bg-white/5 rounded p-0.5" />
          <span className="font-heading font-black text-sm">Back Office</span>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${isActive ? "bg-primary text-white" : "text-white/70 hover:bg-white/10"}`}>
              <n.icon size={18} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10 space-y-3">
          <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-white/5">
            <span className="text-xs text-white/70">Disponible (chat)</span>
            <Switch checked={available} onCheckedChange={toggleAvailable} data-testid="agent-availability" />
          </div>
          <Link to="/" target="_blank" className="flex items-center gap-2 px-3 text-xs text-white/50 hover:text-primary"><ExternalLink size={14} /> Voir le site</Link>
          <button onClick={doLogout} data-testid="admin-logout" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/70 hover:bg-white/10">
            <LogOut size={18} /> Déconnexion
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-[72px] bg-white border-b border-border flex items-center justify-between px-4 sm:px-6 sticky top-0 z-20">
          <button className="lg:hidden p-2" onClick={() => setOpen(true)}><Menu size={22} /></button>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-3">
          <Link to="/admin/account" className="flex items-center gap-3 group" data-testid="header-account-link">
            <div className="text-right">
              <p className="text-sm font-bold text-ink leading-tight group-hover:text-primary transition-colors">{user?.name}</p>
              <p className="text-xs text-ink/50 capitalize">{user?.role}</p>
            </div>
            <div className="h-9 w-9 rounded-full bg-primary text-white flex items-center justify-center font-heading font-black">{user?.name?.[0]}</div>
          </Link>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
